import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, ShieldCheck, Circle, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

function FollowButton({ testId }) {
  const [following, setFollowing] = useState(false);
  return (
    <button onClick={() => setFollowing(!following)} className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${following ? 'bg-[#7C3AED] text-white' : 'text-[#7C3AED] border border-[#7C3AED] hover:bg-[#7C3AED]/5'}`} data-testid={testId}>
      {following ? 'Following' : 'Follow'}
    </button>
  );
}

function LiveActivityTicker() {
  const [transactions, setTransactions] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    fetch('/data/transactions.json').then(r => r.json()).then(d => setTransactions(d.slice(0, 20))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!transactions.length) return;
    const timer = setInterval(() => setIdx(i => (i + 1) % Math.min(transactions.length, 10)), 3000);
    return () => clearInterval(timer);
  }, [transactions]);

  const tx = transactions[idx];
  if (!tx) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="live-activity-ticker">
      <div className="flex items-center gap-2 mb-3">
        <Circle className="w-2 h-2 fill-emerald-500 text-emerald-500 animate-pulse" />
        <span className="text-xs font-semibold text-emerald-600">Live Activity</span>
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={idx} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }} className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-[#7C3AED]/10 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-4 h-4 text-[#7C3AED]" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-slate-700 font-medium truncate">${tx.amount?.toLocaleString()} to {tx.projectTitle?.slice(0, 22)}...</p>
            <p className="text-[10px] text-slate-400">{tx.donorName || 'Anonymous'} &middot; Verified</p>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default function RightSidebar() {
  const [trending, setTrending] = useState({ projects: [], causes: [] });
  const [donors, setDonors] = useState([]);

  useEffect(() => {
    fetch('/data/projects.json').then(r => r.json()).then(projs => {
      const topProjects = [...projs].sort((a, b) => (b.fundingRaised || 0) - (a.fundingRaised || 0)).slice(0, 5);
      const uniqueCauses = [...new Set(projs.map(p => p.causeArea).filter(Boolean))].slice(0, 6);
      setTrending({ projects: topProjects, causes: uniqueCauses });
    }).catch(() => {});
    fetch('/data/donors.json').then(r => r.json()).then(d => setDonors(d.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <aside className="hidden xl:block w-[280px] shrink-0 space-y-4 py-4 pr-4 sticky top-14 h-[calc(100vh-56px)] overflow-y-auto" data-testid="right-sidebar">
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-[#7C3AED]" />
          <span className="text-sm font-semibold text-slate-900">Trending on HumaClub</span>
        </div>
        <div className="space-y-3">
          {trending.projects?.slice(0, 3).map((p, i) => (
            <Link key={p.id} to={`/project/${p.slug}`} className="block group" data-testid={`trending-project-${i}`}>
              <p className="text-sm font-medium text-slate-900 group-hover:text-[#7C3AED] transition-colors truncate">{p.title}</p>
              <p className="text-xs text-slate-400">{p.ngoName} &middot; {Math.round(p.fundingRaised / p.fundingGoal * 100)}% funded</p>
            </Link>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <span className="text-sm font-semibold text-slate-900 block mb-3">People Making Impact</span>
        <div className="space-y-3">
          {donors.map((d, i) => (
            <div key={d.id} className="flex items-center gap-2.5" data-testid={`suggested-person-${i}`}>
              <img src={d.avatar} alt={d.name} className="w-8 h-8 rounded-full" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{d.name}</p>
                <p className="text-xs text-slate-400">{d.projectsSupported} projects</p>
              </div>
              <FollowButton testId={`follow-${i}`} />
            </div>
          ))}
        </div>
      </div>

      <LiveActivityTicker />

      <a href="https://humadashportal.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-3 bg-white rounded-xl border border-slate-200 text-sm font-medium text-[#7C3AED] hover:bg-slate-50 transition-colors" data-testid="sidebar-humadash-right">
        <ExternalLink className="w-4 h-4" /> Open HumaDash Portal
      </a>

      <div className="px-2 text-[10px] text-slate-300 space-x-2">
        <Link to="/principles" className="hover:text-slate-400">Glass Box</Link>
        <span>&middot;</span>
        <a href="https://humatek.io" target="_blank" rel="noopener noreferrer" className="hover:text-slate-400">HumaTek</a>
        <span>&middot;</span>
        <a href="https://humadash.com" target="_blank" rel="noopener noreferrer" className="hover:text-slate-400">HumaDash</a>
      </div>
    </aside>
  );
}
